import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/firebase/config";
import { Timestamp } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import SEODebugger from "@/components/SEODebugger";
import { sendAdminNotificationEmail } from "@/lib/emailService";
import { Share2, ChevronUp } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  contentHtml: string;
  description?: string;
  thumbnailUrl: string;
  author: string;
  createdAt: Timestamp;
}

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [recentBlogs, setRecentBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { toast } = useToast();

  // Email validation function
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle newsletter subscription
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter your email address",
      });
      return;
    }

    if (!isValidEmail(email)) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a valid email address",
      });
      return;
    }

    setIsSubscribing(true);

    try {
      // Check if email already exists
      const subscribersRef = collection(db, "NewsLetterWeb");
      const q = query(
        subscribersRef,
        where("email", "==", email.toLowerCase())
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        toast({
          variant: "destructive",
          title: "Already Subscribed",
          description: "This email is already subscribed to our newsletter",
        });
        setIsSubscribing(false);
        return;
      }

      // Add new subscriber
      await addDoc(subscribersRef, {
        email: email.toLowerCase(),
        createdAt: serverTimestamp(),
        source: "blog_post",
        userAgent: navigator.userAgent,
        subscriptionPage: window.location.pathname,
      });

      // EmailJS configuration
      const serviceId =
        import.meta.env.VITE_EMAILJS_SERVICE_ID || "service_3edbwuj";
      const publicKey =
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "pYRLv9sYDpYiLevTX";

      // Template ID for the admin notification email
      const adminTemplateId =
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "template_y47bxkc";

      // Notify admin only (welcome email to subscriber disabled)
      try {
        await sendAdminNotificationEmail(
          email.toLowerCase(),
          serviceId,
          adminTemplateId,
          publicKey,
          "blog_post"
        );
        console.log("✅ Admin notification email sent successfully");
      } catch (emailError) {
        console.error(
          "⚠️ Email sending failed, but subscription was saved:",
          emailError
        );
        // Don't fail the subscription if emails fail
      }

      toast({
        title: "Thank You!",
        description:
          "Your subscription has been confirmed. We are glad to have you with us.",
      });

      setEmail("");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to subscribe. Please try again later.",
      });
      console.error("Newsletter subscription error:", error);
    } finally {
      setIsSubscribing(false);
    }
  };

  useEffect(() => {
    const fetchBlogAndRecent = async () => {
      if (!id) return;

      try {
        // Fetch current blog post
        const docRef = doc(db, "BlogsWeb", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const currentPost = {
            id: docSnap.id,
            ...docSnap.data(),
          } as BlogPost;
          setPost(currentPost);
        }

        const blogsRef = collection(db, "BlogsWeb");
        const recentQuery = query(
          blogsRef,
          orderBy("createdAt", "desc"),
          limit(4) // Get 4 to exclude current post and show 3
        );
        const recentSnapshot = await getDocs(recentQuery);

        const recentBlogsList = recentSnapshot.docs
          .map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as BlogPost)
          )
          .filter((blog) => blog.id !== id)
          .slice(0, 3);

        setRecentBlogs(recentBlogsList);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    window.scrollTo({ top: 0, behavior: "smooth" });

    fetchBlogAndRecent();
  }, [id]);

  // Dynamic SEO meta tags update with comprehensive coverage
  useEffect(() => {
    if (post) {
      // Extract text content from HTML for better description
      const textContent = post.contentHtml
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim();

      // Generate meta description from content if not provided
      const metaDescription = post.description
        ? post.description.substring(0, 160)
        : textContent.substring(0, 150) + "...";

      // Update page title
      document.title = `${post.title} | Cittasya® Blog - Yoga Nidra & Wellness`;

      // Helper function to update or create meta tag
      const updateMetaTag = (
        selector: string,
        attribute: string,
        content: string
      ) => {
        let element = document.querySelector(selector);
        if (!element) {
          element = document.createElement("meta");
          if (selector.includes("property")) {
            element.setAttribute("property", selector.split('"')[1]);
          } else {
            element.setAttribute("name", selector.split('"')[1]);
          }
          document.head.appendChild(element);
        }
        element.setAttribute("content", content);
      };

      // Update basic meta tags
      updateMetaTag('meta[name="description"]', "content", metaDescription);

      // Update Open Graph tags
      updateMetaTag('meta[property="og:title"]', "content", post.title);
      updateMetaTag(
        'meta[property="og:description"]',
        "content",
        metaDescription
      );
      updateMetaTag(
        'meta[property="og:image"]',
        "content",
        post.thumbnailUrl || "https://cittasya.com/ci.png"
      );
      updateMetaTag(
        'meta[property="og:url"]',
        "content",
        `https://cittasya.com/blog/${post.id}`
      );
      updateMetaTag('meta[property="og:type"]', "content", "article");
      updateMetaTag('meta[property="og:site_name"]', "content", "Cittasya®");
      updateMetaTag('meta[property="article:author"]', "content", post.author);
      updateMetaTag(
        'meta[property="article:published_time"]',
        "content",
        post.createdAt.toDate().toISOString()
      );
      updateMetaTag(
        'meta[property="article:section"]',
        "content",
        "Health & Wellness"
      );

      // Update Twitter Card tags
      updateMetaTag(
        'meta[name="twitter:card"]',
        "content",
        "summary_large_image"
      );
      updateMetaTag('meta[name="twitter:title"]', "content", post.title);
      updateMetaTag(
        'meta[name="twitter:description"]',
        "content",
        metaDescription
      );
      updateMetaTag(
        'meta[name="twitter:image"]',
        "content",
        post.thumbnailUrl || "https://cittasya.com/ci.png"
      );
      updateMetaTag('meta[name="twitter:site"]', "content", "@cittasya");
      updateMetaTag('meta[name="twitter:creator"]', "content", "@cittasya");

      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", `https://cittasya.com/blog/${post.id}`);

      // Generate and update keywords
      const titleWords = post.title
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length > 3);
      const dynamicKeywords = [
        "Cittasya",
        "Yoga Nidra",
        "meditation",
        "mindfulness",
        "stress relief",
        "mental wellness",
        "deep relaxation",
        "guided meditation",
        "blog",
        "article",
        "wellness",
        "health",
        ...titleWords,
      ];

      updateMetaTag(
        'meta[name="keywords"]',
        "content",
        dynamicKeywords.join(", ")
      );

      // Add or update structured data
      let structuredDataScript = document.querySelector(
        'script[type="application/ld+json"][data-blog-post]'
      );
      if (!structuredDataScript) {
        structuredDataScript = document.createElement("script");
        structuredDataScript.setAttribute("type", "application/ld+json");
        structuredDataScript.setAttribute("data-blog-post", "true");
        document.head.appendChild(structuredDataScript);
      }

      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: metaDescription,
        image: [post.thumbnailUrl || "https://cittasya.com/ci.png"],
        author: {
          "@type": "Person",
          name: post.author,
        },
        publisher: {
          "@type": "Organization",
          name: "Cittasya®",
          logo: {
            "@type": "ImageObject",
            url: "https://cittasya.com/cittasya-logo.svg",
            width: 600,
            height: 60,
          },
        },
        datePublished: post.createdAt.toDate().toISOString(),
        dateModified: post.createdAt.toDate().toISOString(),
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://cittasya.com/blog/${post.id}`,
        },
        articleSection: "Health & Wellness",
        keywords: dynamicKeywords.join(", "),
        wordCount: post.contentHtml.replace(/<[^>]*>/g, "").split(/\s+/).length,
        inLanguage: "en-US",
        isAccessibleForFree: true,
        url: `https://cittasya.com/blog/${post.id}`,
      };

      structuredDataScript.textContent = JSON.stringify(jsonLd);

      // Log for debugging (remove in production)
      console.log(`🔍 SEO: Updated meta tags for blog post: "${post.title}"`);
      console.log(`📄 Meta Description: ${metaDescription}`);
      console.log(`🖼️ Featured Image: ${post.thumbnailUrl || "Default image"}`);
    }

    // Cleanup function to reset meta tags and remove blog-specific structured data
    return () => {
      // Reset to default title when leaving the page
      document.title =
        "Cittasya® - Awaken Deep rest with Yoga Nidra | Deep Relaxation & Stress Relief";

      // Remove blog-specific structured data
      const blogStructuredData = document.querySelector(
        'script[data-blog-post="true"]'
      );
      if (blogStructuredData) {
        blogStructuredData.remove();
      }
    };
  }, [post]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-3xl animate-spin">🔆</span>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h1 className="text-2xl text-forest-green">Blog post not found</h1>
      </div>
    );
  }

  // Calculate read time (rough estimate: 200 words per minute)
  const wordCount = post.contentHtml
    .replace(/<[^>]*>/g, "")
    .split(/\s+/).length;
  const readTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));

  // Generate dynamic SEO data for enhanced optimization
  const generateSEOData = () => {
    if (!post) return {};

    // Extract text content from HTML for better description
    const textContent = post.contentHtml
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();

    // Generate meta description from content if not provided
    const metaDescription = post.description
      ? post.description.substring(0, 160)
      : textContent.substring(0, 150) + "...";

    // Generate dynamic keywords from title and content
    const titleWords = post.title
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 3);
    const contentWords = textContent
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 4)
      .slice(0, 10);

    const dynamicKeywords = [
      "Cittasya",
      "Yoga Nidra",
      "meditation",
      "mindfulness",
      "stress relief",
      "mental wellness",
      "deep relaxation",
      "guided meditation",
      "blog",
      "article",
      "wellness",
      "health",
      ...titleWords,
      ...Array.from(new Set(contentWords)),
    ];

    // Create comprehensive structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: metaDescription,
      image: [post.thumbnailUrl || "https://cittasya.com/ci.png"],
      author: {
        "@type": "Person",
        name: post.author,
      },
      publisher: {
        "@type": "Organization",
        name: "Cittasya®",
        logo: {
          "@type": "ImageObject",
          url: "https://cittasya.com/cittasya-logo.svg",
          width: 600,
          height: 60,
        },
      },
      datePublished: post.createdAt.toDate().toISOString(),
      dateModified: post.createdAt.toDate().toISOString(),
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://cittasya.com/blog/${post.id}`,
      },
      articleSection: "Health & Wellness",
      keywords: dynamicKeywords.join(", "),
      wordCount: wordCount,
      timeRequired: `PT${readTimeMinutes}M`,
      inLanguage: "en-US",
      isAccessibleForFree: true,
      url: `https://cittasya.com/blog/${post.id}`,
    };

    return {
      title: `${post.title} | Cittasya® Blog - Yoga Nidra & Wellness`,
      description: metaDescription,
      keywords: dynamicKeywords,
      image: post.thumbnailUrl || "https://cittasya.com/ci.png",
      url: `https://cittasya.com/blog/${post.id}`,
      type: "article",
      author: post.author,
      publishedTime: post.createdAt.toDate().toISOString(),
      modifiedTime: post.createdAt.toDate().toISOString(),
      schema: structuredData,
    };
  };

  return (
    <div className="min-h-screen bg-cloud-white pt-12">
      {post && <SEO {...generateSEOData()} />}
      {/* Back Navigation */}
      <div className="max-w-4xl mx-auto px-4 pt-8 mt-12">
        <Link
          to="/"
          className="inline-flex items-center text-forest-green hover:text-copper-brown transition-colors"
        >
          <span className="mr-2 text-2xl mb-1">←</span>
          Back to Home
        </Link>
      </div>

      <article className="pt-12 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Article Type */}
          <div className="mb-4">
            <span className="text-soft-saffron font-medium">Article</span>
          </div>

          {/* Title Section */}
          <header className="mb-8">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest-green mb-4">
              {post.title}
            </h1>

            {post.description && (
              <p className="text-xl text-copper-brown/80 mb-6">
                {post.description}
              </p>
            )}

            {/* Author and Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-copper-brown/70">
              <div className="flex items-center gap-2">
                {/* <div className="w-10 h-10 rounded-full bg-forest-green/10 flex items-center justify-center">
                  <span className="text-forest-green font-medium">
                    {post.author[0].toUpperCase()}
                  </span>
                </div> */}
                <span className="font-medium text-forest-green">
                  {post.author}
                </span>
              </div>

              <time dateTime={post.createdAt.toDate().toISOString()}>
                {post.createdAt.toDate().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          </header>

          {/* Share Buttons */}

          {/* Featured Image */}
          {post.thumbnailUrl && (
            <div className="mb-12">
              <img
                src={post.thumbnailUrl}
                alt={post.title}
                className="w-full max-h-[400px] object-cover rounded-2xl shadow-lg"
              />
            </div>
          )}

          {/* Content */}
          <div
            className="bg-white rounded-2xl shadow-lg p-8 mb-8"
            data-allow-select="true"
          >
            <div
              className="blog-content prose prose-lg max-w-none select-text
                prose-headings:text-forest-green prose-headings:font-heading 
                prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-6
                prose-h2:text-2xl prose-h2:font-semibold prose-h2:mb-4 prose-h2:mt-8
                prose-h3:text-xl prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-6
                prose-p:text-gray-800 prose-p:leading-relaxed prose-p:mb-6 prose-p:select-text
                [&_a]:!text-blue-600 [&_a]:!underline [&_a]:decoration-2 [&_a]:underline-offset-2 
                [&_a:hover]:!text-blue-800 [&_a:hover]:decoration-blue-800
                [&_a]:transition-colors [&_a]:duration-200 [&_a]:cursor-pointer
                [&>*:first-child]:mt-0
                [&>*:last-child]:mb-0
                [&_*]:select-text"
              style={
                {
                  "--tw-prose-links": "#2563eb",
                  "--tw-prose-links-hover": "#1e40af",
                } as React.CSSProperties
              }
              dangerouslySetInnerHTML={{
                __html: post.contentHtml
                  .replace(/<p>/g, '<p class="text-gray-800 select-text">')
                  .replace(
                    /<a /g,
                    '<a target="_blank" rel="noopener noreferrer" '
                  )
                  .replace(
                    /<a([^>]*?)>/g,
                    '<a$1 style="color: #2563eb !important; text-decoration: underline;" class="hover:!text-blue-800 underline decoration-2 underline-offset-2 transition-colors duration-200 cursor-pointer">'
                  )
                  // Convert URLs that are not already links to clickable links
                  .replace(
                    /(?<!href=["'])(https?:\/\/[^\s<>"']+)/gi,
                    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #2563eb !important; text-decoration: underline;" class="hover:!text-blue-800 underline decoration-2 underline-offset-2 transition-colors duration-200 cursor-pointer">$1</a>'
                  ),
              }}
            />
          </div>

          {/* Newsletter Subscription Section */}
          <div className="bg-gradient-to-br from-forest-green to-forest-green/90 rounded-2xl shadow-lg p-8 mb-8 text-white relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-soft-saffron to-transparent rounded-full transform translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-copper-brown to-transparent rounded-full transform -translate-x-12 translate-y-12"></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-start gap-4 mb-6">
                {/* <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl text-white">🔆</span>
                </div> */}
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-2">
                    Subscribe for More Blogs
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    Join our community of conscious minds. Get the latest
                    insights on Yoga Nidra, meditation, and wellness delivered
                    directly to your inbox.
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-4 max-w-md"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg bg-white border border-gray-300 text-forest-green placeholder:text-forest-green/60 focus:outline-none focus:border-soft-saffron focus:ring-1 focus:ring-soft-saffron transition-all duration-200"
                  disabled={isSubscribing}
                  style={{ color: "#2d5016" }}
                />
                <Button
                  type="submit"
                  disabled={isSubscribing}
                  className="bg-white hover:bg-soft-saffron text-forest-green hover:text-white px-6 py-3 rounded-lg font-medium min-w-[140px] h-12 transition-all duration-200"
                >
                  {isSubscribing ? (
                    <>
                      <span className="animate-spin mr-2">🔆</span>
                      Subscribing...
                    </>
                  ) : (
                    "Subscribe Now"
                  )}
                </Button>
              </form>
              {/* 
              <p className="text-white/70 text-sm mt-4">
                ✨ No spam, just mindful content. Unsubscribe anytime.
              </p> */}
            </div>
          </div>

          {recentBlogs.length > 0 && (
            <div className="mt-16 mb-8">
              <h2 className="text-3xl font-heading font-bold text-forest-green mb-8 text-center">
                Recent Articles
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.id}`}
                    className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  >
                    {blog.thumbnailUrl && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={blog.thumbnailUrl}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}

                    <div className="p-6">
                      <div className="mb-3">
                        <span className="text-soft-saffron text-sm font-medium">
                          Article
                        </span>
                      </div>

                      <h3 className="font-heading text-xl font-bold text-forest-green mb-3 group-hover:text-copper-brown transition-colors duration-200 line-clamp-2">
                        {blog.title}
                      </h3>

                      {blog.description && (
                        <p className="text-copper-brown/80 text-sm mb-4 line-clamp-3">
                          {blog.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm text-copper-brown/60">
                        <span>{blog.author}</span>
                        <time dateTime={blog.createdAt.toDate().toISOString()}>
                          {blog.createdAt.toDate().toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </time>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-forest-green hover:bg-copper-brown text-white p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      {/* Footer */}
      <Footer />

      {/* SEO Debugger - Remove in production */}
      {/* {process.env.NODE_ENV === "development" && <SEODebugger />} */}
    </div>
  );
};

export default BlogPost;
